import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './HighlightsForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function AdmissionsKeyDatesForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [formData, setFormData] = useState({ title: '', subtitle: '', description: '', keyDates: [] });
  const [pendingFields, setPendingFields] = useState({ title: false, subtitle: false, description: false });
  const [pendingDates, setPendingDates] = useState({});
  const [originalData, setOriginalData] = useState({...formData});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [selectedDateForModal, setSelectedDateForModal] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/admissions/cms/sections`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error(response.status);
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const section = result.data.find(s => s.section_type === 'admissions-key-dates');
        if (section) {
          setSectionId(section.id);
          let content = section.content_published;
          if (typeof content === 'string') content = JSON.parse(content);
          const en = content?.en || {};
          
          // Parse dates from database format (dateRange: "2026-01-15 to 2026-02-28" or date: "2026-01-15")
          const parseDates = (dates) => {
            return dates.map(d => {
              let dateStart = '';
              let dateEnd = '';
              
              if (d.dateRange) {
                const parts = d.dateRange.split(' to ');
                dateStart = parts[0]?.trim() || '';
                dateEnd = parts[1]?.trim() || '';
              } else if (d.date) {
                dateStart = d.date;
                dateEnd = d.date;
              }
              
              return {
                title: d.title || '',
                dateStart,
                dateEnd,
                // Don't include 'date' field - it will be calculated on save
                description: d.description || '',
                tag: d.tag || ''
              };
            });
          };
          
          const keyDates = en.dates || en.keyDates || [];
          const newData = {
            title: en.title || '',
            subtitle: en.subtitle || '',
            description: en.description || '',
            keyDates: parseDates(keyDates)
          };
          setFormData(newData);
          setOriginalData(newData);

          const pRes = await fetch(`${apiBase}/cms/section/${section.id}/with-pending`, {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          });
          if (pRes.ok) {
            const pResult = await pRes.json();
            if (pResult.success && pResult.data?.pending_changes) {
              const pMap = { title: false, subtitle: false, description: false };
              const dMap = {};
              pResult.data.pending_changes.forEach(c => {
                if (c.status === 'pending' && c.locale === 'en') {
                  if (['title', 'subtitle', 'description'].includes(c.field_id)) pMap[c.field_id] = true;
                  else if (c.field_id?.includes('keyDates.')) {
                    const m = c.field_id.match(/keyDates\.(\d+)\./);
                    if (m) dMap[`date_${m[1]}`] = true;
                  }
                }
              });
              setPendingFields(pMap);
              setPendingDates(dMap);
            }
          }
        }
      }
    } catch (err) {
      console.error('[AdmissionsKeyDatesForm] Load error:', err.message);
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (name, value) => setFormData(prev => ({...prev, [name]: value}));
  
  const handleDateChange = (idx, field, value) => {
    setFormData(prev => ({
      ...prev,
      keyDates: prev.keyDates.map((d, i) => i === idx ? {...d, [field]: value} : d)
    }));
  };

  const handleSubmitField = async (name) => {
    if (!sectionId) { toast.error('No section'); return; }
    if (!formData[name]) { toast.warning(`Fill ${name}`); return; }
    if (formData[name] === originalData[name]) { toast.warning('No changes'); return; }

    setSubmitting(p => ({...p, [name]: true}));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: name,
          field_name: name,
          old_value: originalData[name],
          new_value: formData[name],
          locale: 'en'
        })
      });
      const result = await res.json();
      if (result.success || res.ok) {
        toast.success('Submitted');
        setPendingFields(p => ({...p, [name]: true}));
        setOriginalData(p => ({...p, [name]: formData[name]}));
      } else toast.error(result.error || 'Failed');
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(p => ({...p, [name]: false}));
    }
  };

  const handleSubmitDate = async (idx) => {
    if (!sectionId) { toast.error('No section'); return; }
    const date = formData.keyDates[idx];
    if (!date?.title) { toast.warning('Fill title'); return; }
    if (!date?.dateStart) { toast.warning('Fill start date'); return; }

    const key = `date_${idx}`;
    setSubmitting(p => ({...p, [key]: true}));
    try {
      // Prepare the data in the correct format for backend
      // Backend expects dateStart and dateEnd, it will convert to dateRange
      const dateToSubmit = {
        title: date.title,
        dateStart: date.dateStart,
        dateEnd: date.dateEnd || date.dateStart, // If no end date, use start date
        description: date.description || '',
        tag: date.tag || ''
        // Don't send 'date' field - let backend build it from dateStart/dateEnd
      };

      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `keyDates.${idx}.update`,
          field_name: `Key Date: ${date.title}`,
          old_value: originalData.keyDates[idx] || {},
          new_value: dateToSubmit,
          locale: 'en'
        })
      });
      const result = await res.json();
      if (result.success || res.ok) {
        toast.success('Date submitted');
        setPendingDates(p => ({...p, [key]: true}));
        setOriginalData(p => ({...p, keyDates: p.keyDates.map((d, i) => i === idx ? dateToSubmit : d)}));
      } else toast.error(result.error || 'Failed');
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(p => ({...p, [key]: false}));
    }
  };

  const handleDeleteDate = (idx) => {
    if (window.confirm('Delete key date?')) {
      setFormData(prev => ({...prev, keyDates: prev.keyDates.filter((_, i) => i !== idx)}));
      setPendingDates(p => { const u = {...p}; delete u[`date_${idx}`]; return u; });
    }
  };

  const handleAddDate = () => {
    setFormData(prev => ({
      ...prev,
      keyDates: [...prev.keyDates, { title: '', dateStart: '', dateEnd: '', description: '', tag: '' }]
    }));
  };

  if (loading) return <div className="hero-form-wrapper"><div className="loading">Loading...</div></div>;

  return (
    <div className="hero-form-wrapper">
      <ToastContainer />
      <div className="hero-form-header">
        <h1 className="hero-form-title">Admissions Key Dates</h1>
        <p className="hero-form-description">Edit title, subtitle, description & manage key dates timeline</p>
      </div>

      {/* Title */}
      <div className="form-section">
        <div className="field-header">
          <label>Title</label>
          {pendingFields.title && <span className="status-badge pending">Pending</span>}
        </div>
        <input type="text" value={formData.title} onChange={e => handleFieldChange('title', e.target.value)} disabled={submitting.title || pendingFields.title} placeholder="Section title..." className="text-input" />
        <button onClick={() => handleSubmitField('title')} disabled={submitting.title || pendingFields.title || formData.title === originalData.title} className="submit-btn">
          {submitting.title ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Subtitle */}
      <div className="form-section">
        <div className="field-header">
          <label>Subtitle</label>
          {pendingFields.subtitle && <span className="status-badge pending">Pending</span>}
        </div>
        <input type="text" value={formData.subtitle} onChange={e => handleFieldChange('subtitle', e.target.value)} disabled={submitting.subtitle || pendingFields.subtitle} placeholder="Subtitle..." className="text-input" />
        <button onClick={() => handleSubmitField('subtitle')} disabled={submitting.subtitle || pendingFields.subtitle || formData.subtitle === originalData.subtitle} className="submit-btn">
          {submitting.subtitle ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Description */}
      <div className="form-section">
        <div className="field-header">
          <label>Description</label>
          {pendingFields.description && <span className="status-badge pending">Pending</span>}
        </div>
        <textarea value={formData.description} onChange={e => handleFieldChange('description', e.target.value)} disabled={submitting.description || pendingFields.description} placeholder="Description..." className="textarea-input" rows="3" />
        <button onClick={() => handleSubmitField('description')} disabled={submitting.description || pendingFields.description || formData.description === originalData.description} className="submit-btn">
          {submitting.description ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Key Dates */}
      <div className="form-section">
        <div className="items-section-header">
          <h2 className="form-section-title">Key Dates</h2>
          <button type="button" onClick={handleAddDate} className="btn-add-item">+ Add Date</button>
        </div>

        {formData.keyDates.length === 0 ? (
          <div className="items-empty-state"><p>No key dates. Click "Add Date"</p></div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1.5rem' }}>
            {formData.keyDates.map((date, idx) => {
              const key = `date_${idx}`;
              const isPending = pendingDates[key];
              return (
                <div key={idx} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  {/* Card Content */}
                  <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#1f2937' }}>
                        {date.title || 'Untitled'}
                      </h3>
                      {date.tag && <span style={{ fontSize: '0.75rem', background: '#dbeafe', color: '#1e40af', padding: '0.25rem 0.5rem', borderRadius: '3px', fontWeight: '600' }}>{date.tag}</span>}
                    </div>
                    {(() => {
                      // Display formatted date (same logic as AdmissionsPage)
                      let displayDate = '';
                      if (date.dateStart && date.dateEnd) {
                        if (date.dateStart === date.dateEnd) {
                          displayDate = new Date(date.dateStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                        } else {
                          displayDate = `${new Date(date.dateStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(date.dateEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
                        }
                      }
                      return displayDate && <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>{displayDate}</p>;
                    })()}
                    {date.description && <p style={{ margin: 0, fontSize: '0.875rem', color: '#4b5563', lineHeight: 1.5 }}>{date.description.substring(0, 70)}...</p>}
                  </div>

                  {/* Card Actions */}
                  <div style={{ padding: '0.75rem', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '0.5rem' }}>
                    <button 
                      type="button"
                      onClick={() => setSelectedDateForModal(idx)}
                      disabled={isPending}
                      style={{ flex: 1, padding: '0.5rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}
                    >
                      Edit
                    </button>
                    <button 
                      type="button"
                      onClick={() => handleDeleteDate(idx)}
                      disabled={isPending}
                      style={{ flex: 1, padding: '0.5rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}
                    >
                      Delete
                    </button>
                  </div>

                  {isPending && <div style={{ padding: '0.5rem', background: '#fef08a', color: '#92400e', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600' }}>Pending Approval</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Date Modal */}
      {selectedDateForModal !== null && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }} onClick={() => setSelectedDateForModal(null)}>
          <div style={{ background: 'white', borderRadius: '12px', maxHeight: '90vh', overflow: 'auto', maxWidth: '700px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={{ padding: '1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white' }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>Edit Key Date</h2>
              <button type="button" onClick={() => setSelectedDateForModal(null)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Title */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Title *</label>
                <input 
                  type="text" 
                  value={formData.keyDates[selectedDateForModal]?.title || ''} 
                  onChange={e => handleDateChange(selectedDateForModal, 'title', e.target.value)} 
                  placeholder="e.g., Applications Open" 
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Start Date */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Start Date *</label>
                <input 
                  type="date" 
                  value={formData.keyDates[selectedDateForModal]?.dateStart || ''} 
                  onChange={e => handleDateChange(selectedDateForModal, 'dateStart', e.target.value)} 
                  placeholder="Start date" 
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* End Date */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>End Date *</label>
                <input 
                  type="date" 
                  value={formData.keyDates[selectedDateForModal]?.dateEnd || ''} 
                  onChange={e => handleDateChange(selectedDateForModal, 'dateEnd', e.target.value)} 
                  placeholder="End date" 
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Preview of how date will be displayed */}
              {(() => {
                const date = formData.keyDates[selectedDateForModal];
                let preview = '';
                if (date?.dateStart && date?.dateEnd) {
                  if (date.dateStart === date.dateEnd) {
                    preview = new Date(date.dateStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  } else {
                    preview = `${new Date(date.dateStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(date.dateEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
                  }
                }
                return preview && (
                  <div style={{ padding: '0.75rem', background: '#e0f2fe', border: '1px solid #0284c7', borderRadius: '6px', fontSize: '0.875rem', color: '#0c4a6e' }}>
                    <strong>Preview:</strong> {preview}
                  </div>
                );
              })()}

              {/* Description */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Description</label>
                <textarea 
                  value={formData.keyDates[selectedDateForModal]?.description || ''} 
                  onChange={e => handleDateChange(selectedDateForModal, 'description', e.target.value)} 
                  placeholder="What happens on this date..." 
                  rows="3"
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Modal Actions */}
              <div style={{ display: 'flex', gap: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                <button 
                  type="button"
                  onClick={() => handleSubmitDate(selectedDateForModal)}
                  disabled={submitting[`date_${selectedDateForModal}`]}
                  style={{ flex: 1, padding: '0.75rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                >
                  {submitting[`date_${selectedDateForModal}`] ? 'Submitting...' : 'Submit'}
                </button>
                <button 
                  type="button"
                  onClick={() => setSelectedDateForModal(null)}
                  style={{ flex: 1, padding: '0.75rem', background: '#6b7280', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
